import React, { useState } from "react";

export interface UploadError {
  row: number;
  farm_id: string;
  device_id: string;
  error: string;
  message: string;
}

export interface UploadResultData {
  inserted: number;
  failed: number;
  errors: UploadError[];
}

interface Props {
  result: UploadResultData;
  onClose: () => void;
}

function downloadCsv(errors: UploadError[]) {
  const rows = [
    ["row", "farm_id", "device_id", "error_code", "message"],
    ...errors.map((e) => [String(e.row), e.farm_id || "", e.device_id || "", e.error || "", e.message || ""]),
  ];
  const csv = rows.map((r) => r.map((c) => `"${c.replace(/"/g, '""')}"`).join(",")).join("\n");
  const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
  const a = Object.assign(document.createElement("a"), { href: url, download: "upload_errors.csv" });
  a.click();
  URL.revokeObjectURL(url);
}

export default function UploadResultModal({ result, onClose }: Props) {
  const [downloadError, setDownloadError] = useState(false);
  const allOk = result.inserted > 0 && result.failed === 0;

  const handleDone = () => {
    if (downloadError && result.errors.length > 0) downloadCsv(result.errors);
    onClose();
  };

  return (
    <>
      <div className="modal-backdrop fade show" style={{ zIndex: 1040 }} onClick={onClose} />
      <div className="modal fade show d-block" style={{ zIndex: 1050 }} role="dialog">
        <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: 560 }}>
          <div className="modal-content border-0" style={{ borderRadius: 16, overflow: "hidden", boxShadow: "0 20px 60px rgba(0,0,0,.22)" }}>

            {/* Header */}
            <div style={{ background: "#3a5c00", padding: "18px 20px 16px", color: "#fff" }}>
              <div className="d-flex align-items-center justify-content-between">
                <div className="text-center" style={{ flex: 1, padding: "0 12px" }}>
                  <div style={{ fontSize: "0.65rem", fontWeight: 700, letterSpacing: "0.12em", opacity: 0.65, textTransform: "uppercase", marginBottom: 3 }}>
                    CSV Upload Result
                  </div>
                  <div style={{ fontSize: "1.15rem", fontWeight: 700 }}>
                    {allOk ? "Upload successful" : result.inserted > 0 ? "Partially complete" : "Upload failed"}
                  </div>
                </div>
                <button onClick={onClose} style={{ width: 34, height: 34, borderRadius: 6, border: "1.5px solid rgba(255,255,255,0.35)", background: "transparent", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="none" stroke="rgba(255,255,255,0.8)" strokeWidth="2.2" viewBox="0 0 24 24">
                    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Body */}
            <div style={{ padding: "20px 20px 4px" }}>
              {result.inserted > 0 && (
                <div style={{ display: "flex", alignItems: "stretch", background: "#f0fce8", border: "1px solid #c3e6a0", borderRadius: 10, overflow: "hidden", marginBottom: 10 }}>
                  <div style={{ minWidth: 72, padding: "14px 0", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", borderRight: "1px solid #c3e6a0" }}>
                    <div style={{ fontSize: "1.6rem", fontWeight: 800, lineHeight: 1, color: "#6D9300" }}>{result.inserted}</div>
                    <div style={{ fontSize: "0.58rem", fontWeight: 700, letterSpacing: "0.08em", color: "#6D9300", textTransform: "uppercase", marginTop: 3 }}>INSERTED</div>
                  </div>
                  <div style={{ flex: 1, padding: "14px 16px", display: "flex", alignItems: "center" }}>
                    <p style={{ margin: 0, fontSize: "0.83rem", color: "#2d5a00", lineHeight: 1.5 }}>
                      <strong>{result.inserted} row{result.inserted !== 1 ? "s" : ""}</strong> inserted successfully.
                    </p>
                  </div>
                </div>
              )}

              {result.failed > 0 && (
                <div style={{ display: "flex", alignItems: "stretch", background: "#fff5f5", border: "1px solid #ffd0d0", borderRadius: 10, overflow: "hidden", marginBottom: 18 }}>
                  <div style={{ minWidth: 72, padding: "14px 0", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", borderRight: "1px solid #ffd0d0" }}>
                    <div style={{ fontSize: "1.6rem", fontWeight: 800, lineHeight: 1, color: "#c0392b" }}>{result.failed}</div>
                    <div style={{ fontSize: "0.58rem", fontWeight: 700, letterSpacing: "0.08em", color: "#c0392b", textTransform: "uppercase", marginTop: 3 }}>FAILED</div>
                  </div>
                  <div style={{ flex: 1, padding: "14px 16px", display: "flex", alignItems: "center" }}>
                    <p style={{ margin: 0, fontSize: "0.83rem", color: "#9b1c1c", lineHeight: 1.5 }}>
                      <strong>All {result.failed} row{result.failed !== 1 ? "s" : ""} failed</strong> to process. Fix duplicates in your CSV and re-upload.
                    </p>
                  </div>
                </div>
              )}

              {result.errors.length > 0 && (
                <>
                  <div style={{ fontSize: "0.68rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", color: "#9ca3af", marginBottom: 8 }}>Failed Rows</div>
                  <div style={{ maxHeight: 260, overflowY: "auto", display: "flex", flexDirection: "column", gap: 6, paddingRight: 2 }}>
                    {result.errors.map((err, idx) => (
                      <div key={idx} style={{ display: "flex", alignItems: "center", gap: 10, background: "#fff", border: "1px solid #f5e6e6", borderRadius: 8, padding: "10px 12px" }}>
                        <div style={{ minWidth: 26, height: 26, borderRadius: "50%", background: "#fff0f0", color: "#c0392b", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "0.68rem", fontWeight: 700, flexShrink: 0 }}>{idx + 1}</div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: "0.8rem", fontWeight: 600, color: "#374151", fontFamily: "monospace", lineHeight: 1.3, wordBreak: "break-all" }}>{err.farm_id || "—"}</div>
                          {err.device_id && <div style={{ fontSize: "0.75rem", fontFamily: "monospace", color: "#6D9300", fontWeight: 500, lineHeight: 1.2, wordBreak: "break-all" }}>{err.device_id}</div>}
                          <div style={{ fontSize: "0.75rem", color: "#6b7280", marginTop: 2 }}>{err.message}</div>
                        </div>
                        <span style={{ fontSize: "0.6rem", fontWeight: 700, letterSpacing: "0.04em", background: "#fff0f0", color: "#c0392b", border: "1px solid #ffd0d0", borderRadius: 4, padding: "3px 7px", flexShrink: 0, whiteSpace: "nowrap" }}>{err.error}</span>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>

            {/* Footer */}
            <div style={{ padding: "16px 20px 20px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              {result.errors.length > 0 ? (
                <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: "0.83rem", color: "#374151", userSelect: "none" }}>
                  <input type="checkbox" checked={downloadError} onChange={(e) => setDownloadError(e.target.checked)} style={{ width: 15, height: 15, accentColor: "#6D9300", cursor: "pointer" }} />
                  Download error report
                </label>
              ) : <span />}
              <button className="btn px-4" style={{ backgroundColor: "#6D9300", color: "white", borderColor: "#6D9300", borderRadius: 8, fontWeight: 600, fontSize: "0.88rem" }} onClick={handleDone}>
                Done
              </button>
            </div>

          </div>
        </div>
      </div>
    </>
  );
}
